import { useRouter } from 'next/router';
import { useCallback } from 'react';

export const CheckoutModes = {
  market: 'market',
  limit: 'limit',
  crossChain: 'cross-chain',
} as const;

export const CHECKOUT_MODE_PER_QUERY_PARAM = {
  'cross-chain': 'crossChain',
  'limit': 'limit',
  'market': 'market',
} as const;

export type CheckoutMode = 'market' | 'limit' | 'cross-chain';

const DEFAULT_CHECKOUT_MODE = CheckoutModes.market;

type UseCheckoutModeState = [CheckoutMode, (newCheckoutMode: CheckoutMode) => void];

/**
 * Hook to get and set the checkout mode.
 * @returns
 */
export default function useCheckoutMode(): UseCheckoutModeState {
  const router = useRouter();

  const {
    query: { mode: checkoutModeQueryParameter },
  } = router;

  let checkoutMode: CheckoutMode = DEFAULT_CHECKOUT_MODE; // default checkout mode;

  if (
    typeof checkoutModeQueryParameter === 'string' &&
    CheckoutModes[CHECKOUT_MODE_PER_QUERY_PARAM[checkoutModeQueryParameter as CheckoutMode]]
  )
    checkoutMode =
      CheckoutModes[CHECKOUT_MODE_PER_QUERY_PARAM[checkoutModeQueryParameter as CheckoutMode]];

  const setCheckoutMode = useCallback(
    (newCheckoutMode: CheckoutMode) => {
      const query: {
        mode?: CheckoutMode;
      } = {
        ...router.query,
        mode: newCheckoutMode,
      };

      // the default checkout mode doesn't require the query parameter.
      if (newCheckoutMode === DEFAULT_CHECKOUT_MODE) delete query.mode; // it looks better without it.

      router.push(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true }, // no need to re-fetch data
      );
    },
    [router],
  );

  return [checkoutMode, setCheckoutMode];
}
